/**
 * @fileoverview Capital Adequacy page — CRAR 5-year trend.
 * @description Ported from the InsightBox Banking design system's own `CapitalAdequacy/Crar5Y.tsx`.
 * CRAR is a bank-level regulatory ratio, never attributable to a branch (see
 * `BankDataPayload.meta.notes.crar`), so unlike every other thin trend page this one has no branch
 * filter at all — only the FY filter.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CrarTrend,
  FISCAL_YEARS,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  core,
  crarMinimum,
  formatPP,
  formatPct,
  monthlySeries,
  resolveFy,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import PageHeader from '@/shared/components/molecules/PageHeader'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'

/**
 * @component
 */
function BankingCapitalAdequacyPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)

  const header = (
    <PageHeader
      kicker={t('banking.capitalAdequacy.kicker')}
      title={t('banking.capitalAdequacy.title')}
      description={t('banking.capitalAdequacy.description')}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const is5Years = fyFilter === FIVE_YEAR_ID
  const fy = resolveFy(fyFilter)

  const current = core(data, fy).crar
  const minimum = crarMinimum(data)
  const buffer = current - minimum
  const priorFy = fy === FISCAL_YEARS[0] ? null : FISCAL_YEARS[FISCAL_YEARS.indexOf(fy) - 1]!
  const priorCrar = priorFy ? core(data, priorFy).crar : null
  const change5y =
    core(data, FISCAL_YEARS[FISCAL_YEARS.length - 1]!).crar - core(data, FISCAL_YEARS[0]).crar
  const peak = FISCAL_YEARS.reduce(
    (best, f) => (core(data, f).crar > core(data, best).crar ? f : best),
    FISCAL_YEARS[0],
  )
  const priorYearImproved = priorCrar !== null && current >= priorCrar

  const chartData = is5Years
    ? FISCAL_YEARS.map((f) => ({
        fy: f,
        crar: core(data, f).crar,
        min: minimum,
        buffer: core(data, f).crar - minimum,
      }))
    : monthlySeries(data, fy, (f) => core(data, f).crar).map((p) => ({
        fy: p.fy,
        crar: p.value,
        min: minimum,
        buffer: p.value - minimum,
      }))

  const kpis: BankingKpiStripItem[] = is5Years
    ? [
        {
          label: t('banking.capitalAdequacy.kpi.currentCrar'),
          value: formatPct(current),
          sublabel: fy,
          highlight: true,
        },
        {
          label: t('banking.capitalAdequacy.kpi.capitalBuffer'),
          value: formatPP(buffer),
          sublabel: t('banking.capitalAdequacy.kpi.aboveMinimum', { minimum }),
        },
        {
          label: t('banking.capitalAdequacy.kpi.fiveYearChange'),
          value: formatPP(change5y),
          sublabel: t('banking.capitalAdequacy.kpi.fiveYearChangeSublabel'),
          trend: { direction: change5y >= 0 ? 'up' : 'down' },
        },
        {
          label: t('banking.capitalAdequacy.kpi.peakCrar'),
          value: formatPct(core(data, peak).crar),
          sublabel: peak,
        },
      ]
    : [
        {
          label: t('banking.capitalAdequacy.kpi.crar'),
          value: formatPct(current),
          sublabel: fy,
          highlight: true,
        },
        {
          label: t('banking.capitalAdequacy.kpi.capitalBuffer'),
          value: formatPP(buffer),
          sublabel: t('banking.capitalAdequacy.kpi.aboveMinimum', { minimum }),
        },
        {
          label: t('banking.capitalAdequacy.kpi.priorYearCrar'),
          value: priorCrar !== null ? formatPct(priorCrar) : '—',
          sublabel: priorFy ?? t('banking.capitalAdequacy.kpi.noPriorYear'),
          ...(priorCrar !== null && { trend: { direction: priorYearImproved ? 'up' : 'down' } }),
        },
        {
          label: t('banking.capitalAdequacy.kpi.fiveYearPeakCrar'),
          value: formatPct(core(data, peak).crar),
          sublabel: peak,
        },
      ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        fill
        title={
          is5Years
            ? t('banking.capitalAdequacy.chartTitleFiveYear')
            : t('banking.capitalAdequacy.chartTitleMonthly', { fy })
        }
        subtitle={is5Years ? '' : t('banking.capitalAdequacy.chartSubtitleMonthly')}
      >
        <div className="relative min-h-0 flex-1">
          <CrarTrend key={is5Years ? '5y' : 'monthly'} data={chartData} height="100%" />
        </div>
      </ChartCard>

      <BusinessSummaryPanel
        title={t('banking.businessSummary')}
        lines={
          is5Years
            ? [
                t('banking.capitalAdequacy.summary.fiveYearLine1', {
                  current: formatPct(current),
                  fy,
                  buffer: formatPP(buffer),
                  minimum,
                }),
                t('banking.capitalAdequacy.summary.fiveYearLine2', {
                  direction:
                    change5y >= 0
                      ? t('banking.capitalAdequacy.summary.expanded')
                      : t('banking.capitalAdequacy.summary.compressed'),
                  firstYearBuffer: formatPP(core(data, FISCAL_YEARS[0]).crar - minimum),
                  buffer: formatPP(buffer),
                  outcome:
                    change5y >= 0
                      ? t('banking.capitalAdequacy.summary.strengthening')
                      : t('banking.capitalAdequacy.summary.declining'),
                }),
              ]
            : [
                t('banking.capitalAdequacy.summary.fiveYearLine1', {
                  current: formatPct(current),
                  fy,
                  buffer: formatPP(buffer),
                  minimum,
                }),
                t('banking.capitalAdequacy.summary.yearLine2'),
              ]
        }
      />
    </div>
  )
}

export default BankingCapitalAdequacyPage
